import React, { useEffect, useState, Children, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Wind, Sun, Heart, CheckCircle2, Moon, Activity, Shield, Calendar, BookOpen, Utensils, Check, X, Sparkles, ChevronDown, Star, Quote } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const fadeIn = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 1,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
export function App() {
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // Initialize Meta Pixel
  useEffect(() => {
    if (window.fbq) return;
    const fbq: any = function () {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    script.onload = () => {
      window.fbq('init', '855275007473565');
      window.fbq('track', 'PageView');
    };
    setTimeout(() => {
      if (window.fbq && window.fbq.loaded) {
        window.fbq('init', '855275007473565');
        window.fbq('track', 'PageView');
      }
    }, 100);
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  const handlePurchaseClick = () => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Desbloquea tu Energía Femenina',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
    window.location.href = 'https://pay.hotmart.com/A104327505P';
  };
  return <div className="min-h-screen bg-stone-950 text-stone-200 overflow-x-hidden selection:bg-terracotta/30 font-sans" data-id="element-0">
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-20 lg:pt-24 lg:pb-32 px-6 overflow-hidden" data-id="element-1">
        {/* Background Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-terracotta/5 blur-[120px] rounded-full pointer-events-none" data-id="element-2"></div>

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10" data-id="element-3">
          {/* Left Column: Copy */}
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-left order-2 lg:order-1" data-id="element-4">
            {/* Badge */}
            <motion.div variants={fadeInUp} className="flex justify-start mb-6" data-id="element-5">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-stone-900 border border-stone-800 text-terracotta text-sm font-medium tracking-wide uppercase" data-id="element-6">
                <Sparkles className="w-3.5 h-3.5" data-id="element-7" />
                Energía Femenina
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif leading-[1.1] mb-6 text-stone-50" data-id="element-8">
              Recupera tu vitalidad en 21 días — <br data-id="element-9" />
              <span className="text-terracotta italic" data-id="element-10">
                con microhábitos simples
              </span>
            </motion.h1>

            {/* Parenthetical Hook */}
            <motion.p variants={fadeInUp} className="text-lg text-stone-400 font-light italic mb-6" data-id="element-11">
              (aunque hoy sientas que no tienes energía para nada)
            </motion.p>

            {/* Description */}
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-stone-300 max-w-xl mb-10 font-light leading-relaxed" data-id="element-12">
              Una guía paso a paso de 21 días para mujeres que quieren recuperar
              su energía sin dietas extremas, sin rutinas imposibles... usando
              la ciencia de los microhábitos.
            </motion.p>

            {/* CTA Button */}
            <motion.div variants={fadeInUp} className="flex flex-col items-start gap-4" data-id="element-13">
              <button onClick={scrollToPricing} className="w-full sm:w-auto bg-terracotta text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-terracotta-hover transition-all duration-300 flex items-center justify-center gap-2 group shadow-lg shadow-terracotta/20 hover:shadow-terracotta/30 hover:-translate-y-1" data-id="element-14">
                Quiero mi guía "Energía Femenina"
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-15" />
              </button>

              {/* Trust Line */}
              <p className="text-xs md:text-sm text-stone-500 flex flex-wrap items-center gap-2 pl-2" data-id="element-16">
                <span data-id="element-17">⬇ Acceso inmediato</span>
                <span className="w-1 h-1 rounded-full bg-stone-700" data-id="element-18"></span>
                <span data-id="element-19">Hecho para principiantes</span>
                <span className="w-1 h-1 rounded-full bg-stone-700" data-id="element-20"></span>
                <span data-id="element-21">Sin dietas extremas</span>
              </p>
            </motion.div>

            {/* Benefit Bullets */}
            <motion.div variants={fadeInUp} className="mt-10 space-y-3" data-id="element-22">
              {['Elimina el cansancio crónico y la niebla mental', 'Recupera energía estable durante todo el día', 'Aprende microhábitos que toman solo 2 minutos', 'Plan completo de 21 días (paso a paso)'].map((benefit, i) => <div key={i} className="flex items-start gap-3" data-id="element-23">
                  <CheckCircle2 className="w-5 h-5 text-terracotta shrink-0 mt-0.5" data-id="element-24" />
                  <span className="text-stone-300 font-light" data-id="element-25">{benefit}</span>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Right Column: Image */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="relative order-1 lg:order-2" data-id="element-26">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-stone-800 bg-stone-900 aspect-[4/5] md:aspect-square lg:aspect-[4/5] max-w-md mx-auto lg:max-w-none group" data-id="element-27">
              <img src="https://cdn.magicpatterns.com/uploads/42KB1AoPRRpqMvYMjcRGyx/Imagen_1.png" alt="Ebook Desbloquea tu Energía Femenina" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="eager" data-id="element-28" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-transparent pointer-events-none" data-id="element-29"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EMPATHY SECTION */}
      <section className="py-24 px-6 bg-[#1a1418] border-y border-terracotta/10 relative overflow-hidden" data-id="element-30">
        {/* Subtle pink glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-terracotta/5 blur-[120px] rounded-full pointer-events-none" data-id="element-31"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-32">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-10%'
        }} variants={staggerContainer} data-id="element-33">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-12 text-center text-stone-100" data-id="element-34">
              ¿Te reconoces en esto?
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10" data-id="element-35">
              <motion.div variants={fadeInUp} className="space-y-4" data-id="element-36">
                <EmpathyItem text="Te levantas cansada aunque hayas dormido 8 horas." data-id="element-37" />
                <EmpathyItem text="Tu mente está en una niebla constante que no te deja concentrarte." data-id="element-38" />
                <EmpathyItem text="Te irritas por cosas pequeñas y luego te sientes culpable." data-id="element-39" />
              </motion.div>
              <motion.div variants={fadeInUp} className="space-y-4 md:mt-8" data-id="element-40">
                <EmpathyItem text="Sientes una desconexión total de tu cuerpo y sus necesidades." data-id="element-41" />
                <EmpathyItem text="Has probado dietas y rutinas estrictas que abandonas a los 3 días." data-id="element-42" />
                <EmpathyItem text="Vives esperando el fin de semana solo para dormir." data-id="element-43" />
              </motion.div>
            </div>

            <motion.p variants={fadeInUp} className="text-center text-lg text-stone-400 mt-12 italic max-w-2xl mx-auto" data-id="element-44">
              No es tu culpa. Es tu sistema nervioso pidiendo una pausa real, no
              solo "descanso".
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* TRUTH SECTION */}
      <section className="py-32 px-6 bg-stone-950" data-id="element-45">
        <div className="max-w-3xl mx-auto" data-id="element-46">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-10%'
        }} variants={staggerContainer} data-id="element-47">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-10 text-stone-100" data-id="element-48">
              La mentira de la fuerza de voluntad
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-8 text-lg md:text-xl font-light text-stone-300" data-id="element-49">
              <p data-id="element-50">
                Nos han enseñado que si no logramos nuestros objetivos, es
                porque no nos esforzamos lo suficiente. Que debemos levantarnos
                a las 5 AM, beber café y empujar hasta el agotamiento.
              </p>
              <p data-id="element-51">
                Pero la realidad es diferente. Cuando tu sistema nervioso está
                agotado, la "disciplina" es solo otra forma de violencia contra
                ti mismo.
              </p>
              <div className="relative pl-6 border-l-2 border-terracotta py-2" data-id="element-52">
                <p className="text-xl md:text-2xl font-serif italic text-terracotta" data-id="element-53">
                  No necesitas más reglas. Necesitas recuperar tu vitalidad
                  biológica.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MECHANISM SECTION */}
      <section className="py-32 px-6 bg-stone-900 border-y border-stone-800/50" data-id="element-54">
        <div className="max-w-6xl mx-auto" data-id="element-55">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-56">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-6 text-stone-100" data-id="element-57">
              Por qué los microhábitos funcionan
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-58">
              La armonización hormonal no requiere cambios drásticos. Requiere
              señales constantes de seguridad para tu cuerpo.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-59">
            {/* Image */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="lg:col-span-5 order-2 lg:order-1" data-id="element-60">
              <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-black/30 border border-stone-800 aspect-[4/5]" data-id="element-61">
                <img src="https://cdn.magicpatterns.com/uploads/rp1XRfbda49EtkajGdmemk/Imagen_2.png" alt="Detalle del ebook en tablet" className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500" loading="lazy" data-id="element-62" />
                <div className="absolute inset-0 bg-stone-950/10" data-id="element-63"></div>
              </div>
            </motion.div>

            {/* Cards */}
            <div className="lg:col-span-7 grid grid-cols-1 gap-6 order-1 lg:order-2" data-id="element-64">
              <MechanismCard icon={<Shield className="w-6 h-6" data-id="element-66" />} title="Sin Motivación Extrema" description="Las acciones pequeñas no activan la resistencia mental. No necesitas 'ganas' para hacer algo que toma 2 minutos." delay={0.1} data-id="element-65" />
              <MechanismCard icon={<Activity className="w-6 h-6" data-id="element-68" />} title="Estabilización Hormonal" description="Reducimos los picos de cortisol y glucosa suavemente, permitiendo que tu energía se mantenga estable todo el día." delay={0.2} data-id="element-67" />
              <MechanismCard icon={<Moon className="w-6 h-6" data-id="element-70" />} title="Ritmos Naturales" description="Trabajamos a favor de tu reloj biológico, no en contra. El sueño profundo y la energía diurna son consecuencias naturales." delay={0.3} data-id="element-69" />
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH SECTION */}
      <section className="py-32 px-6 bg-stone-950" data-id="element-71">
        <div className="max-w-5xl mx-auto" data-id="element-72">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-73">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 text-stone-100" data-id="element-74">
              Un retorno a lo natural
            </h2>
            <p className="text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-75">
              Dejamos de luchar contra el cuerpo y empezamos a trabajar con él.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-76">
            <Feature icon={<Wind className="w-6 h-6" data-id="element-78" />} title="Respiración" description="Aprende a usar tu respiración para cambiar tu estado nervioso en minutos, no en años." delay={0.1} data-id="element-77" />
            <Feature icon={<Sun className="w-6 h-6" data-id="element-80" />} title="Ritmos" description="Alinea tu descanso y actividad con la luz del sol. Tu biología espera señales, dáselas." delay={0.2} data-id="element-79" />
            <Feature icon={<Heart className="w-6 h-6" data-id="element-82" />} title="Conexión" description="La energía no solo viene de la comida, viene de sentirte seguro en tu propio cuerpo." delay={0.3} data-id="element-81" />
          </div>
        </div>
      </section>

      {/* TIMELINE SECTION */}
      <section className="py-32 px-6 bg-stone-900 border-y border-stone-800/50 overflow-hidden" style={{
      backgroundColor: '#140d2b'
    }} data-id="element-83">
        <div className="max-w-5xl mx-auto" data-id="element-84">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-85">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 text-stone-100" data-id="element-86">
              Tu camino de recuperación
            </h2>
            <p className="text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-87">
              Lo que puedes esperar cuando dejas de forzar y empiezas a fluir.
            </p>
          </motion.div>

          <div className="relative" data-id="element-88">
            {/* Connecting Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-stone-800 -translate-x-1/2 hidden md:block" data-id="element-89"></div>
            <div className="absolute left-4 top-0 bottom-0 w-px bg-stone-800 md:hidden" data-id="element-90"></div>

            <div className="space-y-12 md:space-y-24 relative" data-id="element-91">
              <TimelineItem day="Día 7" title="El despertar suave" benefits={['Menos cansancio al sonar la alarma', 'Primeras señales de energía estable por la tarde', 'Disminución de la ansiedad nocturna']} align="left" data-id="element-92" />
              <TimelineItem day="Día 14" title="Claridad mental" benefits={['La niebla mental empieza a disiparse', 'Menos irritabilidad con tu entorno', 'Capacidad de concentrarte por periodos más largos']} align="right" data-id="element-93" />
              <TimelineItem day="Día 21" title="Energía renovada" benefits={['Energía estable durante todo el día', 'Sueño profundo y verdaderamente reparador', 'Te sientes tú misma otra vez, conectada y en calma']} align="left" isLast data-id="element-94" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-32 px-6 bg-[#1a1418] relative overflow-hidden" data-id="element-95">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-terracotta/5 blur-[150px] rounded-full pointer-events-none" data-id="element-96"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-97">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-98">
            <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-99">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-terracotta/10 border border-terracotta/20 text-terracotta text-sm font-medium tracking-wide mb-6" data-id="element-100">
                <Star className="w-3.5 h-3.5" data-id="element-101" />
                Testimonios reales
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-100" data-id="element-102">
                Lo que dicen quienes ya lo vivieron
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-103">
              <TestimonialCard quote="Después de años sintiéndome agotada, por fin entendí que no era flojera. En la primera semana ya sentí un cambio real en mi energía por las tardes." name="Carolina M." detail="Día 10 del programa" delay={0.1} data-id="element-104" />
              <TestimonialCard quote="Los microhábitos son tan simples que no tienes excusa para no hacerlos. Eso es lo brillante. Empecé durmiendo mejor y todo lo demás mejoró solo." name="Valentina R." detail="Completó los 21 días" delay={0.2} data-id="element-105" />
              <TestimonialCard quote="Dejé de sentir esa niebla mental constante. Ahora puedo concentrarme en mi trabajo y llego al final del día sin sentirme destruida." name="Sofía L." detail="Día 18 del programa" delay={0.3} data-id="element-106" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-32 px-6 bg-stone-950" data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-109">
            <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-110">
              <h2 className="text-3xl md:text-4xl font-serif text-stone-100 mb-4" data-id="element-111">
                Preguntas frecuentes
              </h2>
              <p className="text-lg text-stone-400" data-id="element-112">
                Todo lo que necesitas saber antes de comenzar.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="space-y-4" data-id="element-113">
              {[{
              q: '¿Qué formato tiene el ebook?',
              a: 'Es un ebook digital en formato PDF que puedes leer desde tu celular, tablet o computadora. Recibes acceso inmediato después de la compra.'
            }, {
              q: '¿Necesito experiencia previa en bienestar o salud?',
              a: 'No. Está diseñado para principiantes. Cada microhábito se explica paso a paso y toma solo 2 minutos. No necesitas conocimientos previos.'
            }, {
              q: '¿Cuánto tiempo necesito dedicarle al día?',
              a: 'Los microhábitos están diseñados para tomar entre 2 y 5 minutos al día. La idea es que sean tan simples que no activen tu resistencia mental.'
            }, {
              q: '¿Qué pasa si no veo resultados?',
              a: 'Ofrecemos una garantía de satisfacción de 7 días. Si el contenido no cumple tus expectativas, te devolvemos el 100% de tu dinero sin preguntas.'
            }, {
              q: '¿Puedo compartir el ebook con alguien más?',
              a: 'El ebook es de uso personal y no puede ser redistribuido. Si conoces a alguien que lo necesite, puedes compartirle el enlace de compra.'
            }].map((faq, i) => <div key={i} className="rounded-2xl border border-stone-800 overflow-hidden transition-colors duration-300 hover:border-stone-700" data-id="element-114">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 md:p-6 text-left gap-4" data-id="element-115">
                    <span className="text-lg font-medium text-stone-200" data-id="element-116">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-terracotta shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-117" />
                  </button>
                  <AnimatePresence data-id="element-118">
                    {openFaq === i && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1]
                }} className="overflow-hidden" data-id="element-119">
                        <p className="px-5 md:px-6 pb-5 md:pb-6 text-stone-400 font-light leading-relaxed" data-id="element-120">
                          {faq.a}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="py-40 px-6 bg-stone-950 text-center relative overflow-hidden" data-id="element-121">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-stone-900 to-stone-950 opacity-50" data-id="element-122"></div>
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-4xl mx-auto relative z-10" data-id="element-123">
          <p className="text-3xl md:text-5xl font-serif leading-tight mb-8 text-stone-100" data-id="element-124">
            "La verdadera productividad es el resultado natural de un cuerpo
            descansado y una mente en calma."
          </p>
        </motion.div>
      </section>

      {/* PRICING CTA SECTION */}
      <section id="pricing" className="py-32 px-6 bg-stone-900 flex flex-col items-center scroll-mt-20" data-id="element-125">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-5xl w-full" data-id="element-126">
          <div className="bg-stone-800/50 rounded-3xl p-6 md:p-12 shadow-2xl shadow-black/40 border border-stone-700/50 overflow-hidden backdrop-blur-sm" data-id="element-127">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-128">
              {/* Product Image */}
              <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3] lg:aspect-square border border-stone-700/50" data-id="element-129">
                <img src="https://cdn.magicpatterns.com/uploads/42KB1AoPRRpqMvYMjcRGyx/Imagen_1.png" alt="Ebook Desbloquea tu Energía Femenina" className="w-full h-full object-cover" loading="lazy" data-id="element-130" />
              </motion.div>

              {/* Product Details */}
              <div className="space-y-8" data-id="element-131">
                <div data-id="element-132">
                  <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-4 text-stone-100 leading-tight" data-id="element-133">
                    Desbloquea tu <br data-id="element-134" />
                    <span className="text-terracotta" data-id="element-135">Energía Femenina</span>
                  </motion.h2>
                  <motion.p variants={fadeInUp} className="text-lg text-stone-400" data-id="element-136">
                    21 días de microhábitos para volver a sentirte tú, sin
                    dietas extremas ni rutinas imposibles.
                  </motion.p>
                </div>

                <motion.div variants={fadeInUp} className="space-y-4" data-id="element-137">
                  <h3 className="font-medium text-stone-300 uppercase tracking-wide text-sm" data-id="element-138">
                    Lo que incluye:
                  </h3>
                  <ul className="space-y-3" data-id="element-139">
                    <BonusItem icon={<BookOpen className="w-5 h-5" data-id="element-141" />} text="Ebook Completo: El sistema paso a paso" data-id="element-140" />
                  </ul>
                </motion.div>

                <div className="bg-stone-900/50 rounded-2xl p-6 md:p-8 border border-stone-700/50" data-id="element-142">
                  <motion.div variants={fadeInUp} data-id="element-143">
                    <div className="flex justify-between items-end mb-6" data-id="element-144">
                      <div data-id="element-145">
                        <p className="text-stone-400 text-sm font-medium uppercase tracking-wider mb-1" data-id="element-146">
                          Oferta Especial
                        </p>
                        <div className="flex items-baseline gap-3" data-id="element-147">
                          <span className="text-4xl md:text-5xl font-serif text-stone-50" data-id="element-148">
                            $6.39
                          </span>
                          <span className="text-stone-500 line-through text-xl" data-id="element-149">
                            $19.99
                          </span>
                        </div>
                      </div>
                    </div>

                    <button onClick={handlePurchaseClick} className="w-full bg-terracotta text-stone-50 px-6 py-4 rounded-xl text-lg font-medium hover:bg-terracotta-hover transition-all duration-300 shadow-lg shadow-terracotta/20 hover:shadow-terracotta/30 hover:scale-[1.02] mb-4" data-id="element-150">
                      Comenzar mi transformación
                    </button>

                    <div className="space-y-2 text-xs text-stone-500 text-center" data-id="element-151">
                      <p className="flex items-center justify-center gap-1" data-id="element-152">
                        <Shield className="w-3 h-3" data-id="element-153" /> Pago 100% seguro
                      </p>
                      <p data-id="element-154">Acceso inmediato • Garantía de satisfacción</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 text-center bg-stone-950 border-t border-stone-900" data-id="element-155">
        <div className="max-w-4xl mx-auto" data-id="element-156">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-4" data-id="element-157">
            <button onClick={() => setModalContent('privacy')} className="text-stone-500 hover:text-terracotta transition-colors duration-300 text-sm font-medium" data-id="element-158">
              Política de Privacidad
            </button>
            <span className="hidden md:block text-stone-700" data-id="element-159">•</span>
            <button onClick={() => setModalContent('terms')} className="text-stone-500 hover:text-terracotta transition-colors duration-300 text-sm font-medium" data-id="element-160">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-stone-600 text-sm" data-id="element-161">
            &copy; {new Date().getFullYear()} Vitalidad. Hecho con intención.
          </p>
        </div>
      </footer>

      {/* MODAL */}
      <AnimatePresence data-id="element-162">
        {modalContent && <Modal onClose={() => setModalContent(null)} data-id="element-163">
            {modalContent === 'privacy' ? <PrivacyContent data-id="element-164" /> : <TermsContent data-id="element-165" />}
          </Modal>}
      </AnimatePresence>
    </div>;
}
// --- HELPER COMPONENTS ---
function EmpathyItem({
  text
}: {
  text: string;
}) {
  return <div className="flex items-start gap-4 p-4 rounded-xl bg-terracotta/5 border border-terracotta/10 hover:border-terracotta/20 transition-colors duration-300" data-id="element-166">
      <div className="mt-1 bg-terracotta/15 p-1 rounded-full shrink-0" data-id="element-167">
        <CheckCircle2 className="w-5 h-5 text-terracotta" data-id="element-168" />
      </div>
      <p className="text-lg text-stone-300 font-light leading-relaxed" data-id="element-169">
        {text}
      </p>
    </div>;
}
function MechanismCard({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="bg-stone-800/50 p-6 md:p-8 rounded-2xl shadow-lg border border-stone-700/50 hover:border-stone-600 transition-all duration-300 flex flex-col md:flex-row gap-6 items-start" data-id="element-170">
      <div className="w-12 h-12 rounded-full bg-stone-900 border border-stone-700 flex-shrink-0 flex items-center justify-center text-terracotta" data-id="element-171">
        {icon}
      </div>
      <div data-id="element-172">
        <h3 className="text-xl font-serif mb-2 text-stone-100" data-id="element-173">{title}</h3>
        <p className="text-stone-400 leading-relaxed font-light" data-id="element-174">
          {description}
        </p>
      </div>
    </motion.div>;
}
function Feature({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="flex flex-col items-center text-center p-6 rounded-2xl bg-stone-900/50 border border-stone-800 hover:border-stone-700 transition-all duration-300" data-id="element-175">
      <div className="w-14 h-14 rounded-full bg-stone-800 flex items-center justify-center text-terracotta mb-6 border border-stone-700" data-id="element-176">
        {icon}
      </div>
      <h3 className="text-2xl font-serif mb-3 text-stone-100" data-id="element-177">{title}</h3>
      <p className="text-stone-400 leading-relaxed font-light" data-id="element-178">{description}</p>
    </motion.div>;
}
function TimelineItem({
  day,
  title,
  benefits,
  align,
  isLast
}: {
  day: string;
  title: string;
  benefits: string[];
  align: 'left' | 'right';
  isLast?: boolean;
}) {
  const isLeft = align === 'left';
  return <motion.div initial={{
    opacity: 0,
    x: isLeft ? -20 : 20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true,
    margin: '-10%'
  }} transition={{
    duration: 0.8
  }} className={`flex flex-col md:flex-row items-center md:justify-between gap-8 md:gap-0 ${isLeft ? '' : 'md:flex-row-reverse'}`} data-id="element-179">
      {/* Content Side */}
      <div className={`w-full md:w-[45%] pl-12 md:pl-0 ${isLeft ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`} data-id="element-180">
        <span className="inline-block px-3 py-1 bg-terracotta/10 text-terracotta rounded-full text-sm font-medium mb-3 border border-terracotta/20" data-id="element-181">
          {day}
        </span>
        <h3 className="text-2xl font-serif text-stone-100 mb-4" data-id="element-182">{title}</h3>
        <ul className={`space-y-2 ${isLeft ? 'md:items-end' : 'md:items-start'} flex flex-col`} data-id="element-183">
          {benefits.map((benefit, idx) => <li key={idx} className="text-stone-400 font-light flex items-center gap-2" data-id="element-184">
              <span className="w-1.5 h-1.5 rounded-full bg-stone-600 flex-shrink-0 md:hidden" data-id="element-185"></span>
              {benefit}
              <span className={`w-1.5 h-1.5 rounded-full bg-stone-600 flex-shrink-0 hidden md:block ${isLeft ? '' : 'order-first'}`} data-id="element-186"></span>
            </li>)}
        </ul>
      </div>

      {/* Center Marker */}
      <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-terracotta bg-stone-900 z-10 shadow-[0_0_10px_rgba(212,129,111,0.5)]" data-id="element-187">
        <div className="w-2 h-2 bg-terracotta rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-188"></div>
      </div>

      {/* Empty Side for Balance */}
      <div className="hidden md:block w-[45%]" data-id="element-189"></div>
    </motion.div>;
}
function BonusItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <li className="flex items-center gap-3 text-stone-300" data-id="element-190">
      <div className="text-terracotta bg-terracotta/10 p-1.5 rounded-md border border-terracotta/20" data-id="element-191">
        {icon}
      </div>
      <span className="font-light" data-id="element-192">{text}</span>
    </li>;
}
function TestimonialCard({
  quote,
  name,
  detail,
  delay
}: {
  quote: string;
  name: string;
  detail: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="bg-stone-900/60 p-6 md:p-8 rounded-2xl border border-terracotta/10 flex flex-col justify-between gap-6" data-id="element-193">
      <div data-id="element-194">
        <Quote className="w-8 h-8 text-terracotta/30 mb-4" data-id="element-195" />
        <p className="text-stone-300 font-light leading-relaxed italic" data-id="element-196">
          "{quote}"
        </p>
      </div>
      <div className="flex items-center gap-3 pt-4 border-t border-stone-800" data-id="element-197">
        <div className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta font-serif text-lg" data-id="element-198">
          {name.charAt(0)}
        </div>
        <div data-id="element-199">
          <p className="text-stone-200 font-medium text-sm" data-id="element-200">{name}</p>
          <p className="text-stone-500 text-xs" data-id="element-201">{detail}</p>
        </div>
      </div>
    </motion.div>;
}
function Modal({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-202">
      <motion.div initial={{
      scale: 0.95,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.95,
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="bg-stone-900 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-stone-800" onClick={e => e.stopPropagation()} data-id="element-203">
        <div className="sticky top-0 bg-stone-900 border-b border-stone-800 px-6 py-4 flex justify-between items-center z-10" data-id="element-204">
          <h2 className="text-2xl font-serif text-stone-100" data-id="element-205">
            {children && (children as any).type === PrivacyContent ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-800 rounded-full transition-colors duration-300" data-id="element-206">
            <X className="w-5 h-5 text-stone-400" data-id="element-207" />
          </button>
        </div>
        <div className="px-6 py-8 overflow-y-auto max-h-[calc(80vh-80px)] text-stone-300" data-id="element-208">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
// Keep content components exactly as they were but with updated text colors for dark mode
function PrivacyContent() {
  return <div className="prose prose-invert prose-stone max-w-none" data-id="element-209">
      <p className="text-stone-400 mb-6" data-id="element-210">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-211">
        1. Información que Recopilamos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-212">
        Al adquirir "Desbloquea tu Energía Femenina", recopilamos únicamente la
        información necesaria para procesar tu compra y entregarte el producto
        digital: nombre, correo electrónico e información de pago (procesada de
        forma segura por nuestro proveedor de pagos).
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-213">
        2. Uso de la Información
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-214">Utilizamos tu información para:</p>
      <ul className="list-disc pl-6 text-stone-300 mb-6 space-y-2" data-id="element-215">
        <li data-id="element-216">Procesar y entregar tu compra del ebook</li>
        <li data-id="element-217">Enviarte el acceso inmediato al contenido digital</li>
        <li data-id="element-218">Proporcionar soporte relacionado con tu compra</li>
        <li data-id="element-219">
          Enviarte actualizaciones importantes sobre el producto (opcional)
        </li>
      </ul>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-220">
        3. Protección de Datos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-221">
        Implementamos medidas de seguridad para proteger tu información
        personal. No compartimos, vendemos ni alquilamos tus datos a terceros.
        Tu información de pago es procesada de forma segura por proveedores
        certificados y nunca almacenamos datos de tarjetas de crédito.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-222">
        4. Tus Derechos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-223">
        Tienes derecho a acceder, corregir o eliminar tu información personal en
        cualquier momento. Para ejercer estos derechos, contáctanos y
        responderemos en un plazo de 48 horas.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-224">5. Cookies</h3>
      <p className="text-stone-300 mb-6" data-id="element-225">
        Utilizamos cookies esenciales para el funcionamiento del sitio y para
        mejorar tu experiencia de compra. No utilizamos cookies de seguimiento
        publicitario.
      </p>
    </div>;
}
function TermsContent() {
  return <div className="prose prose-invert prose-stone max-w-none" data-id="element-226">
      <p className="text-stone-400 mb-6" data-id="element-227">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-228">
        1. Aceptación de Términos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-229">
        Al adquirir "Desbloquea tu Energía Femenina", aceptas estos términos y
        condiciones. Este ebook es un producto digital diseñado para uso
        personal y no puede ser redistribuido, revendido o compartido.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-230">
        2. Licencia de Uso
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-231">
        Se te otorga una licencia personal, no transferible y no exclusiva para
        usar el ebook y sus materiales complementarios (checklists, guías,
        tracker). No puedes:
      </p>
      <ul className="list-disc pl-6 text-stone-300 mb-6 space-y-2" data-id="element-232">
        <li data-id="element-233">Compartir, copiar o distribuir el contenido</li>
        <li data-id="element-234">Modificar o crear trabajos derivados</li>
        <li data-id="element-235">Usar el contenido con fines comerciales</li>
        <li data-id="element-236">Revender o transferir tu acceso a terceros</li>
      </ul>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-237">
        3. Entrega del Producto
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-238">
        Recibirás acceso inmediato al ebook y todos los bonuses digitales tras
        completar tu compra. El acceso se enviará al correo electrónico
        proporcionado. Si no recibes el acceso en 10 minutos, verifica tu
        carpeta de spam o contáctanos.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-239">
        4. Garantía de Satisfacción
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-240">
        Ofrecemos una garantía de satisfacción de 7 días. Si el contenido no
        cumple con tus expectativas, contáctanos para solicitar un reembolso
        completo. Solo pedimos que nos compartas brevemente tu experiencia para
        mejorar el producto.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-241">
        5. Descargo de Responsabilidad
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-242">
        Este ebook proporciona información educativa sobre bienestar y
        microhábitos. No sustituye el consejo médico profesional. Consulta con
        un profesional de la salud antes de realizar cambios significativos en
        tu estilo de vida, especialmente si tienes condiciones médicas
        preexistentes.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-243">
        6. Propiedad Intelectual
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-244">
        Todo el contenido del ebook, incluyendo texto, imágenes, diseño y
        materiales complementarios, está protegido por derechos de autor.
        Cualquier uso no autorizado constituye una violación de estos derechos.
      </p>
    </div>;
}